import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useShiftsAssignment, useShiftSeriesEditStore } from '@libs/store/shifts';
import { useLocationStore } from '@libs/store/locations';
import { useNotification } from '@libs/snackbar';
import { parseError } from '@libs/api/errors';
import { ComingSoonSpacer } from '@organisms/dataDisplay';
import { AnimatedTabPanel } from '@molecules/layout';
import { Box, Divider, Flex } from '@atoms/layout';
import { ArchivedShiftIcon, FaIcon, HotShiftIcon } from '@atoms/icons';
import { ShiftHistory } from './components/shiftHistoryLog';
import { ShiftBilling } from './components/shiftBilling';
import { ShiftsList } from './components/shiftsList';
import { ShiftNotes } from './components/shiftNotes';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { EditSeriesForm, PeriodicShiftDrawerControls, ShiftSeriesFormData } from '../components';
import { ExternalIdLabel } from '@molecules/dataDisplay';
import { Chip, Skeleton, Stack, Tooltip } from '@mui/material';
import { ShiftRecurrenceChip, TooltipNote } from '@atoms/dataDisplay';
import { DelayedLinearProgress } from '@molecules/feedback';
import { unassignShift } from '@libs/api/shifts';
import { ConfirmCreateDoubleShiftDialog } from '@pages/schedule/dialogs';
import { AgenciesPage } from '../shareShifts/shareShiftsDrawer';

type SeriesDetailsDrawerProps = {
  onSaved?: () => void;
};
export function SeriesDetailsDrawer({ onSaved }: SeriesDetailsDrawerProps) {
  const opened = useShiftSeriesEditStore((state) => state.drawerOpen);
  const close = useShiftSeriesEditStore((state) => state.closeShiftSeriesDrawer);

  return (
    <Drawer open={opened} onClose={close} anchor="right">
      <DrawerContent onSaved={onSaved} />
    </Drawer>
  );
}

type DrawerContentProps = {
  onSaved?: () => void;
};
function DrawerContent({ onSaved }: DrawerContentProps) {
  const theme = useTheme();
  const { showErrorIntl, showSuccess } = useNotification();
  const pageEnablingMap = {
    generalInfo: true,
    shiftNotes: true,
    shiftsBilling: false,
    shiftHistory: false,
  };
  const { refreshTrigger } = useRefreshSyncStore();
  const [tab, setTab] = useState(0);
  const [hideControls, setHideControls] = useState(false);
  const shift = useShiftSeriesEditStore((state) => state.data);
  const id = shift?.id;
  const seriesShifts = shift?.events || [];
  const seriesLoading = useShiftSeriesEditStore((state) => state.loading);
  const editSeries = useShiftSeriesEditStore((s) => s.saveSeries);
  const closeDrawer = useShiftSeriesEditStore((s) => s.closeShiftSeriesDrawer);
  const { assignShift, checkAssignOverlap } = useShiftsAssignment();

  const loadShift = useShiftSeriesEditStore((s) => s.load);
  const assignSubmitting = useShiftSeriesEditStore((s) => s.assignSubmitting);
  const currentLocationID = useLocationStore((s) => s.currentLocationID);
  const localizationSubset = 'shifts.editSeriesDrawer';

  const loading = seriesLoading;

  const [sharingProgress, setSharingProgress] = useState(false);
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [currentAssigneeId, setCurrentAssigneeId] = useState<string | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [doubleShiftId, setDoubleShiftId] = useState<string | null>(null);

  const isMultiple = shift && shift.allowedMultipleAssignees === true && !loading;
  const isSingle = shift && shift.allowedMultipleAssignees === false && !loading; // shift.allowedMultipleAssignees is nullable !!!
  const isArchive = shift && shift.isArchived && !loading;
  const isUrgent = shift && shift.isUrgent && !loading;
  const isShared = shift && shift.isShared && !loading;
  const hasTags = shift && shift.tags && shift.tags.length > 0 && !loading;
  const isMultiAssignForm = !(shift && isShared && shift.allowedMultipleAssignees === false);
  const canUnassign = shift && shift.canUnassign && !loading;

  const formData = useMemo(
    () =>
      shift
        ? {
            id: shift.id,
            location: currentLocationID || '',
            department: shift.department || '',
            subDepartment: shift.subDepartment || '',
            specialty: shift.speciality || '',
            staffingType: shift.staffingType,
            qualifications: shift.qualifications.map((q) => q.id) || [],
            assignees: shift.assignees.map((a) => a.id),
            tags: shift.tags.join(', '),
          }
        : null,
    [currentLocationID, shift],
  );

  const handleSubmit = async (data: ShiftSeriesFormData) => {
    if (!shift) {
      throw new Error('EditSeriesForm: currentSeriesDetails is not defined');
    }
    try {
      await editSeries(shift.id, {
        locationId: data.location,
        departmentNodeId: data.department,
        subDepartment: {
          isEmpty: !data.subDepartment,
          id: data.subDepartment ? data.subDepartment : null,
        },
        speciality: {
          isEmpty: !data.specialty,
          id: data.specialty ? data.specialty : null,
        },
        staffingType: data.staffingType,
        staffNurseQualificationFilterIds: data.qualifications,
        assignee: {
          isEmpty: !data.assignees.length,
          id: data.assignees[0] || null,
          // TODO: add unassignReason
          unassignReason: null,
        },
        tags: data.tags.split(',').map((tag) => tag.trim()),
        note: shift.notes.map((n) => n.value).join(''),
      });
      showSuccess(<FormattedMessage id="shifts.updateSuccessful" />);
      closeDrawer();
      onSaved?.();
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };
  const reloadShift = useCallback(async () => {
    if (id) {
      await loadShift(id);
    }
  }, [loadShift, id, refreshTrigger]);

  const handleAssignShifts = async (selectedShifts: string[], assigneeId: string) => {
    if (!shift) {
      throw new Error('EditSeriesForm: currentSeriesDetails is not defined');
    }

    setSharingProgress(true);
    for (let i = 0; i < selectedShifts.length; i++) {
      const shiftId = selectedShifts[i];
      const shift = seriesShifts.find((s) => s.id === shiftId);
      if (shift?.id) {
        const overlapInfo = await checkAssignOverlap({
          shiftID: shift.id,
          assigneeId: assigneeId,
        });
        if (overlapInfo.canCreateDoubleShift) {
          setSelectedShifts(selectedShifts);
          setCurrentAssigneeId(assigneeId);
          setDoubleShiftId(shiftId);
          setOpenConfirmDialog(true);
          setSharingProgress(false);
          return; // interrupt all actions in case if overlap is detected
        }
      }
    }

    try {
      await performAssign(selectedShifts, assigneeId);
      await reloadShift();
      setSelectedShifts([]);
      setCurrentAssigneeId(null);
      setDoubleShiftId(null);
      setOpenConfirmDialog(false);
      setSharingProgress(false);
      showSuccess(<FormattedMessage id="shifts.updateSuccessful" />);
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  const performAssign = async (shiftIds: string[], assigneeId: string, doubleShiftId?: string | null) => {
    if (!isMultiAssignForm) {
      // assign entire recurrence
      await assignShift(shift.id, assigneeId, doubleShiftId);
    } else {
      for (const shiftId of shiftIds) {
        // bulk assign of the recurrence children
        await assignShift(shiftId, assigneeId, doubleShiftId);
      }
    }
  };

  const handleUnassignShift = async (shiftId: string, reason: string, comment: string) => {
    try {
      await unassignShift(shiftId, reason, comment);
      await reloadShift();
      showSuccess(<FormattedMessage id="shifts.updateSuccessful" />);
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  const onNoteSave = async (note: { value: string }) => {
    if (!shift) {
      throw new Error('EditSeriesForm: currentSeriesDetails is not defined');
    }
    try {
      await editSeries(shift.id, {
        locationId: shift.locationId,
        staffingType: shift.staffingType,
        tags: shift.tags,
        staffNurseQualificationFilterIds: shift?.qualifications.map((qual) => qual.id) || [],
        assignee: null, // Assignee is not editable in this form
        speciality: {
          isEmpty: !shift.speciality,
          id: shift.speciality ?? null,
        },
        subDepartment: {
          isEmpty: !shift.subDepartment,
          id: shift.subDepartment ?? null,
        },
        departmentNodeId: shift?.department || null,
        note: note.value,
      });
      await reloadShift();
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  return (
    <Stack width={{ xs: '300px', sm: '650px' }} pt={2} direction={'column'} spacing={1} height="100%">
      <Stack px={2} direction="row" alignItems={'center'} spacing={1} height="24px">
        {loading && (
          <>
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
            <Skeleton variant="circular" width={12} height={12} />
          </>
        )}
        {isUrgent && (
          <Tooltip title="Series is urgent" placement="top">
            <Box width={13} height={15}>
              <HotShiftIcon />
            </Box>
          </Tooltip>
        )}
        {isArchive && (
          <Tooltip title="Series is archived" placement="top">
            <Box width={13} height={15}>
              <ArchivedShiftIcon />
            </Box>
          </Tooltip>
        )}
        {isSingle && (
          <Tooltip title="Series assumes single assignee" placement="top">
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="user" height={'10px'} width={'10px'} color="primary.main" />
            </Box>
          </Tooltip>
        )}
        {isMultiple && (
          <Tooltip title="Series allows multiple assignees" placement="top">
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="users" height={'15px'} width={'15px'} color="primary.main" />
            </Box>
          </Tooltip>
        )}
        {isShared && (
          <Tooltip title="Series is shared" placement="top">
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="share-nodes" height={'13px'} width={'13px'} color="success.main" />
            </Box>
          </Tooltip>
        )}
        {hasTags && (
          <TooltipNote
            title={shift.tags.map((tag, idx) => (
              <Chip key={idx} label={tag.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
            popoverTooltipSx={{ px: 2, maxWidth: '300px' }}
          >
            <Box width="13px" height="23px">
              <FaIcon variant="duotone" name="tags" height={'13px'} width={'13px'} color="primary.main" />
            </Box>
          </TooltipNote>
        )}
        <Box flex={1} />
        {loading ? (
          <Skeleton variant="rounded" width={80} height={20} />
        ) : (
          <ShiftRecurrenceChip variant="outlined" type="series" label={'Series'} size="small" />
        )}
        {loading ? (
          <Skeleton variant="rounded" width={120} height={20} />
        ) : shift?.externalId ? (
          <ExternalIdLabel
            localizationSubset={`shifts.drawers.seriesDetails.externalId`}
            externalId={shift?.externalId}
          />
        ) : null}
      </Stack>
      {loading || sharingProgress ? <DelayedLinearProgress show={loading} /> : <Divider />}
      <Box px={2}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="shifts tabs">
          <Tab
            disabled={loading}
            label={<FormattedMessage id="shifts.shiftsTable" values={{ number: shift?.events.length ?? 0 }} />}
            {...a11yProps('table')}
          />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.generalInfo" />} {...a11yProps('info')} />
          <Tab disabled={loading} label={'Shared with'} {...a11yProps('sharedWith')} />
          <Tab disabled={loading} label={<FormattedMessage id="shifts.notes" />} {...a11yProps('notes')} />
          <Tab disabled label={<FormattedMessage id="shifts.billing" />} {...a11yProps('billing')} />
          <Tab disabled label={<FormattedMessage id="shifts.historyLog" />} {...a11yProps('historyLog')} />
        </Tabs>
      </Box>
      <Flex height="100%" overflow="auto">
        <Box position="relative" width="100%">
          <AnimatedTabPanel value={tab} index={0}>
            <ShiftsList
              list={shift?.events.map((event) => ({
                ...event,
                // Some checks in the columns needs this info
                isParentArchived: shift?.isArchived || false,
              }))}
              loading={loading || assignSubmitting}
              isMultiAssign={isMultiAssignForm}
              canUnassign={!!canUnassign}
              parentShift={shift}
              onAssignClick={handleAssignShifts}
              onUnassignClick={handleUnassignShift}
              onShiftChanged={reloadShift}
              locationId={shift?.locationId}
              qualifications={shift?.qualifications.map((q) => q.id) || []}
            />
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={1}>
            <EditSeriesForm
              onSave={handleSubmit}
              data={formData}
              localizationSubset={localizationSubset}
              loading={loading}
              onFormStateChanged={(s) => setHideControls(s.isDirty)}
            />
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={2}>
            <Flex column mx={2} py={2} height="100%" overflow="auto">
              <AgenciesPage
                showSearch={false}
                showSelectedAgenciesCount={false}
                localizationSubset={localizationSubset}
                selectedRows={shift?.agenciesForShare.map((a) => a.id) || []}
                disableSelection
              />
            </Flex>
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={3}>
            <Flex column mx={2} py={2} height="100%" overflow="auto">
              <ShiftNotes
                notes={shift?.notes || []}
                onSave={onNoteSave}
                localizationSubset="shifts.drawers.seriesDetails.notes"
              />
            </Flex>
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={4}>
            <ComingSoonSpacer disableContent={!pageEnablingMap.shiftsBilling}>
              <ShiftBilling shiftId={shift?.id ?? ''} />
            </ComingSoonSpacer>
          </AnimatedTabPanel>
          <AnimatedTabPanel value={tab} index={5}>
            <ComingSoonSpacer disableContent={!pageEnablingMap.shiftHistory}>
              <ShiftHistory shiftId={shift?.id ?? ''} />
            </ComingSoonSpacer>
          </AnimatedTabPanel>
        </Box>
      </Flex>

      <PeriodicShiftDrawerControls
        hide={hideControls}
        loading={loading || sharingProgress}
        onClose={closeDrawer}
        shift={shift}
        localizationSubset="shifts.editSeriesDrawer"
        onUpdated={reloadShift}
      />
      <ConfirmCreateDoubleShiftDialog
        open={openConfirmDialog}
        onClose={() => {
          setDoubleShiftId(null);
          setOpenConfirmDialog(false);
        }}
        onSubmit={async () => {
          try {
            await performAssign(selectedShifts, currentAssigneeId as string, doubleShiftId);
            await reloadShift();
            setSelectedShifts([]);
            setCurrentAssigneeId(null);
            setDoubleShiftId(null);
            setOpenConfirmDialog(false);
            showSuccess(<FormattedMessage id="shifts.updateSuccessful" />);
          } catch (error) {
            showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
          }
        }}
      />
    </Stack>
  );
}

function a11yProps(name: string) {
  return {
    id: `shift-tab-${name}`,
    'aria-controls': `shift-tabpanel-${name}`,
  };
}
